import moment from "moment";
import React, { useState, useMemo } from "react";
import { FiChevronDown, FiChevronUp, FiDownload, FiFilter } from "react-icons/fi";
import { Lock } from "lucide-react";

import {
  DateNavigation,
  FilterPanel,
  ViewToggle,
} from "../../../components/Fixtures";
import SharedEmptyState from "../../../components/EmptyState";
import emptyStateImage from "../../../assets/images/fixtures-empty-state.svg";
import FixtureCard from "../../../components/Fixtures/FixtureCard";
import CategoryTabs from "../format/components/CategoryTabs";

import ResultsCalendarView from "./ResultsCalendarView";

const ResultsViewV2 = ({
  fixturesByDate = {},
  sortedDates = [],
  selectedTournament,
  startDate,
  endDate,
  onDateChange,
  onShiftDates,
  onAddResult,
  onEditFixture,
  getMatchTeams,
  selectedCategory,
  onCategoryChange,
  lockedPhaseIds = new Set(),
  isOwner = false,
  onOpenLockStages,
}) => {
  const [showConflicts, setShowConflicts] = useState(false);
  const [isFiltersOpen, setIsFiltersOpen] = useState(false);
  const [isCalendarView, setIsCalendarView] = useState(false);
  const [collapsedDates, setCollapsedDates] = useState({});
  const [selectedPhases, setSelectedPhases] = useState([]);
  const [selectedPitches, setSelectedPitches] = useState([]);

  const toggleDateCollapse = (date) => {
    setCollapsedDates(prev => ({ ...prev, [date]: !prev[date] }));
  };

  const filterOptions = useMemo(() => {
    if (!selectedCategory?.phases) return { phases: [], pitches: [] };
    const phases = selectedCategory.phases.map((p) => p.name) || [];
    const pitches = selectedTournament?.matchVenue?.pitches || [];
    return { phases, pitches };
  }, [selectedCategory, selectedTournament]);

  const activeFiltersCount = selectedPhases.length + selectedPitches.length;

  const handlePhaseChange = (phase, isAdd) => {
    setSelectedPhases(prev => isAdd ? [...prev, phase] : prev.filter(p => p !== phase));
  };

  const handlePitchChange = (pitch, isAdd) => {
    setSelectedPitches(prev => isAdd ? [...prev, pitch] : prev.filter(p => p !== pitch));
  };

  const handleClearAllFilters = () => {
    setSelectedPhases([]);
    setSelectedPitches([]);
    setShowConflicts(false);
  };

  const filteredFixturesByDate = useMemo(() => {
    const filtered = {};
    Object.entries(fixturesByDate).forEach(([date, fixtures]) => {
      const filteredFixtures = fixtures.filter((fixture) => {
        if (!fixture) return false;
        if (fixture.status === "CANCELLED") return false;
        if (selectedPhases.length > 0 && !selectedPhases.includes(fixture.phaseName)) return false;
        if (selectedPitches.length > 0 && !selectedPitches.includes(fixture.pitch)) return false;
        return true;
      });
      if (filteredFixtures.length > 0) filtered[date] = filteredFixtures;
    });
    return filtered;
  }, [fixturesByDate, selectedPhases, selectedPitches]);

  const filteredSortedDates = useMemo(() => {
    return Object.keys(filteredFixturesByDate).sort(
      (a, b) => moment(b, "YYYY-MM-DD").valueOf() - moment(a, "YYYY-MM-DD").valueOf()
    );
  }, [filteredFixturesByDate]);

  const exportToCSV = () => {
    const headers = ["Date", "Time", "Home Team", "Away Team", "Pitch", "Phase", "Group"];
    const rows = [];
    Object.entries(fixturesByDate).forEach(([date, fixtures]) => {
      fixtures.forEach((fixture) => {
        const startMoment = fixture.matchTime ? moment(fixture.matchTime) : null;
        rows.push([
          startMoment ? startMoment.format("YYYY-MM-DD") : (date || ""),
          startMoment ? startMoment.format("HH:mm") : "",
          fixture.homeTeam?.title || fixture.homeTeam?.name || "TBD",
          fixture.awayTeam?.title || fixture.awayTeam?.name || "TBD",
          fixture.pitch || "",
          fixture.phaseName || "",
          fixture.groupName || "",
        ]);
      });
    });
    const csvContent = [headers, ...rows]
      .map(row => row.map(cell => `"${String(cell ?? "")}"`).join(","))
      .join("\n");
    const blob = new Blob(["\uFEFF" + csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", `results-${selectedTournament?.name || "export"}.csv`);
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const isEmpty = filteredSortedDates.length === 0;

  return (
    <div className={`p-3 sm:p-[18px] lg:px-8 flex flex-col gap-5 ${isEmpty ? "h-full" : ""}`}>

      <div className="flex items-center justify-between">
        <h1 className="text-[22px] font-normal text-[#FEFCEB]">Results</h1>
        <div className="flex items-center gap-3 opacity-0">
            <div
              className="px-4 py-2 text-[15px] font-medium text-app-text bg-app-card rounded-full hover:text-[#FEFCEB] transition-colors"
            >
              dummy
            </div>
            <button
              className="bg-[#00E7FF] hover:opacity-50 text-[#021529] text-[15px] font-medium px-4 py-2 rounded-full transition-all active:scale-95"
            >
              dummy
            </button>
        </div>
      </div>

      {/* Category Tabs + Lock stages */}
      {selectedTournament?.categories && selectedTournament.categories.length > 0 && (
        <div className="flex items-center justify-between">
          <CategoryTabs
            categories={selectedTournament.categories}
            selectedCategory={selectedCategory?.label}
            onCategoryChange={onCategoryChange}
          />
          {onOpenLockStages && (
            <button
              type="button"
              onClick={onOpenLockStages}
              className="flex items-center gap-2 px-4 py-2 text-[15px] font-medium text-app-text bg-app-card rounded-full hover:text-[#FEFCEB] transition-colors cursor-pointer"
            >
              <Lock size={14} />
              Lock stages
            </button>
          )}
        </div>
      )}

      {/* Calendar or List View */}
      {isCalendarView ? (
        <ResultsCalendarView
          fixturesByDate={filteredFixturesByDate}
          sortedDates={filteredSortedDates}
          selectedTournament={selectedTournament}
          selectedCategory={selectedCategory}
          startDate={startDate}
          endDate={endDate}
          onDateChange={onDateChange}
          onShiftDates={onShiftDates}
          isCalendarView={isCalendarView}
          onToggleView={() => setIsCalendarView(false)}
          onOpenFilters={() => setIsFiltersOpen(true)}
          activeFiltersCount={activeFiltersCount}
          onAddResult={onAddResult}
          onEditFixture={onEditFixture}
          getMatchTeams={getMatchTeams}
          showConflicts={showConflicts}
          lockedPhaseIds={lockedPhaseIds}
          isOwner={isOwner}
        />
      ) : (
        <div className={isEmpty ? "flex-1 min-h-0 flex flex-col" : ""}>
          {/* Date Navigation & Actions Row */}

          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 sm:h-[44px] mb-4">
            <DateNavigation
              startDate={startDate ? new Date(startDate) : null}
              endDate={endDate ? new Date(endDate) : null}
              onDateChange={(start, end) => {
                if (start) onDateChange?.("start", moment(start).format("YYYY-MM-DD"));
                if (end) onDateChange?.("end", moment(end).format("YYYY-MM-DD"));
              }}
              onShiftDates={onShiftDates}
            />
            <div className="flex items-center gap-2 flex-wrap">
              <button
                onClick={exportToCSV}
                className="w-11 h-11 bg-app-card rounded-full flex items-center justify-center hover:opacity-50 transition-opacity"
                title="Download CSV"
              >
                <FiDownload className="w-5 h-5 text-[#FEFCEB]" />
              </button>
              <button
                onClick={() => setIsFiltersOpen(true)}
                className="h-11 px-4 bg-app-card rounded-full flex items-center gap-2 hover:opacity-50 transition-opacity"
              >
                <FiFilter className="w-5 h-5 text-[#FEFCEB]" />
                <span className="text-[#FEFCEB] font-medium text-[15px]">
                  Filters{activeFiltersCount > 0 && ` (${activeFiltersCount})`}
                </span>
              </button>
              <ViewToggle isCalendar={false} onToggle={() => setIsCalendarView(true)} />
            </div>
          </div>

          {/* List View Content */}
          {filteredSortedDates.length === 0 ? (
            <SharedEmptyState
              illustration={emptyStateImage}
              heading="No fixtures yet"
              description="Get started quickly with auto-generated fixtures, or create your own manually in the fixtures tab."
            />
          ) : (
            <div className="space-y-4 mt-4">
              {filteredSortedDates.map((date) => {
                const fixtures = filteredFixturesByDate[date] || [];
                if (fixtures.length === 0) return null;
                return (
                  <div key={date} className="bg-blue-shade-8 p-3 rounded-2xl">
                    <div
                      className="flex items-center gap-2 cursor-pointer group select-none"
                      onClick={() => toggleDateCollapse(date)}
                    >
                      <div className="w-6 h-6 flex items-center justify-center rounded-full hover:bg-white/10 transition-colors">
                        {collapsedDates[date] ? (
                          <FiChevronDown className="w-5 h-5 text-[#FEFCEB]" />
                        ) : (
                          <FiChevronUp className="w-5 h-5 text-[#FEFCEB]" />
                        )}
                      </div>
                      <h3 className="text-[#FEFCEB] text-[16px] font-medium m-0">
                        {moment(date, "YYYY-MM-DD").format("ddd, D MMM")}
                      </h3>
                    </div>

                    {!collapsedDates[date] && (
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mt-4">
                        {fixtures.map((fixture) => {
                          const matchTeams = getMatchTeams?.(fixture) || { homeTeam: {}, awayTeam: {} };
                          const fixtureWithTeams = {
                            ...fixture,
                            homeTeam: { ...fixture.homeTeam, ...matchTeams.homeTeam },
                            awayTeam: { ...fixture.awayTeam, ...matchTeams.awayTeam },
                            hasPitchConflict: matchTeams.hasPitchConflict,
                            hasEndTimeConflict: matchTeams.hasEndTimeConflict,
                          };
                          return (
                            <FixtureCard
                              key={fixture._id || fixture.id}
                              fixture={fixtureWithTeams}
                              result={fixture.eventId || null}
                              onEdit={onEditFixture}
                              onAddResult={onAddResult}
                              onEditResult={onAddResult}
                              showConflicts={showConflicts}
                              hideDate
                              isReadOnly={true}
                              isLocked={lockedPhaseIds.has(fixture.phaseId)}
                              isOwner={isOwner}
                            />
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Filter Panel */}
      <FilterPanel
        isOpen={isFiltersOpen}
        onClose={() => setIsFiltersOpen(false)}
        showConflicts={showConflicts}
        setShowConflicts={setShowConflicts}
        phases={filterOptions.phases}
        selectedPhases={selectedPhases}
        onPhaseChange={handlePhaseChange}
        pitches={filterOptions.pitches}
        selectedPitches={selectedPitches}
        onPitchChange={handlePitchChange}
        onClearAll={handleClearAllFilters}
        onApply={() => setIsFiltersOpen(false)}
      />
    </div>
  );
};

export default ResultsViewV2;
